import React, { useState } from 'react';
import { Button } from './SubscriptionAppButton';
import { Input } from './SubscriptionAppInput';
import { CheckCircle, X } from 'lucide-react';
interface ModeChangeRequestModalProps {
  /** Which change is being requested — the copy and fields differ per direction */
  direction: 'to-loyalty-only' | 'add-club';
  /** What the merchant is asking for, used for the modal heading */
  title: string;
  /** Prefilled winery name from the store record */
  defaultWineryName: string;
  /** Prefilled contact email from the store record */
  defaultContactEmail: string;
  onClose: () => void;
  onSubmitted: () => void;
}
export function ModeChangeRequestModal({
  direction,
  title,
  defaultWineryName,
  defaultContactEmail,
  onClose,
  onSubmitted
}: ModeChangeRequestModalProps) {
  const [wineryName, setWineryName] = useState(defaultWineryName);
  const [contactEmail, setContactEmail] = useState(defaultContactEmail);
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [memberNumbers, setMemberNumbers] = useState('');
  const [partner, setPartner] = useState('');
  const [goal, setGoal] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const toLoyaltyOnly = direction === 'to-loyalty-only';
  const handleSubmit = () => {
    setSubmitted(true);
    onSubmitted();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0F172A]/40 p-4" role="dialog" aria-modal="true" aria-label={title} data-id="element-3530" data-mp="2z9jgev502yje">
      <div className="w-full max-w-[560px] max-h-[90vh] overflow-y-auto bg-white rounded-lg border border-[#CBD5E1] shadow-sm" data-id="element-3531" data-mp="2v74x69xf5wrl">
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-start justify-between gap-4" data-id="element-3532" data-mp="2qaxowahjw96h">
          <h2 className="text-[18px] font-semibold text-[#0F172A]" data-id="element-3533" data-mp="2lbuvxoeuhtev">
            {submitted ? 'Request received' : title}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-[#94A3B8] hover:text-[#0F172A] transition-colors" data-id="element-3534" data-mp="jhvkn31q3c26">
            <X className="w-5 h-5" data-id="element-3535" data-mp="2dx0zl6s3woeg" />
          </button>
        </div>

        {submitted ? <div className="p-6" data-id="element-3536" data-mp="2iqdm9bd86l4k">
            <div className="flex items-start gap-3" data-id="element-3537" data-mp="2iqn3oak7fjyn">
              <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" data-id="element-3538" data-mp="2417ev048holl" />
              <p className="text-[14px] text-[#475569] m-0" data-id="element-3539" data-mp="28z95q77wis7m">
                We've received your request. Someone from the WinePik team will
                be in touch to book a call and walk through the change with you.
              </p>
            </div>
            <div className="mt-6 flex justify-end" data-id="element-3540" data-mp="31nes0yelsp9k">
              <Button variant="primary" onClick={onClose} data-id="element-3541" data-mp="2mrp42upuy227">
                Close
              </Button>
            </div>
          </div> : <div className="p-6 space-y-4" data-id="element-3542" data-mp="2rope1gptvoyl">
            {toLoyaltyOnly ? <p className="text-[14px] text-[#64748B] m-0" data-id="element-3543" data-mp="1bb8wfnkjoppw">
                We make this change with you rather than it being an instant
                switch, because we need to reconfigure the app for you. We turn
                off the club side of the interface so you only see loyalty —
                subscriptions, club builder and allocations are hidden. Nothing
                is deleted, and we check what happens to any members currently
                on a club before anything changes.
              </p> : <p className="text-[14px] text-[#64748B] m-0" data-id="element-3544" data-mp="3c1rdcxr4uwb">
                We set this up with you so nothing goes wrong with your existing
                members. Tell us a little about what you want to run and we'll
                take it from there.
              </p>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3545" data-mp="3kkf2mh597qsx">
              <Input label="Winery name" value={wineryName} onChange={e => setWineryName(e.target.value)} data-id="element-3546" data-mp="3klq60fusewji" />
              <Input type="email" label="Contact email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} data-id="element-3547" data-mp="3frlvxnq868e7" />
              <Input label="Contact name" value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Who should we speak to?" data-id="element-3548" data-mp="2o2sfppztov4m" />
              <Input type="tel" label="Phone" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Best number to reach you" data-id="element-3549" data-mp="30tcp02v9f2x9" />
              {!toLoyaltyOnly && <>
                  <Input label="Current or expected members" value={memberNumbers} onChange={e => setMemberNumbers(e.target.value)} placeholder="e.g. 450" data-id="element-3550" data-mp="3aowdw5bep1ci" />
                  <Input label="Agency or partner (optional)" value={partner} onChange={e => setPartner(e.target.value)} placeholder="Who you work with" data-id="element-3551" data-mp="3kfr39ijy8h26" />
                </>}
            </div>

            <div data-id="element-3552" data-mp="3oj6zwew0qhev">
              <label htmlFor="mode-request-goal" className="block text-[13px] font-medium text-[#475569] mb-1.5" data-id="element-3553" data-mp="3tgozvfs25maq">
                {toLoyaltyOnly ? "What's prompting the change?" : 'What do you want to run?'}
              </label>
              <textarea id="mode-request-goal" value={goal} onChange={e => setGoal(e.target.value)} rows={4} placeholder="Tell us what you have in mind" className="w-full px-3 py-2.5 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent transition-shadow resize-none" data-id="element-3554" data-mp="1z3ftvxyq0ix8" />
            </div>

            <div className="flex items-center justify-end gap-3 pt-2" data-id="element-3555" data-mp="1msyap7kz2wuh">
              <Button variant="ghost" onClick={onClose} data-id="element-3556" data-mp="1htv7lsojz0p5">
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSubmit} data-id="element-3557" data-mp="2fh3zq6j27804">
                Send request
              </Button>
            </div>
          </div>}
      </div>
    </div>;
}